import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { ProductBlockHeading } from "./ProductBlockHeading";
import { ProductEditorialIcon } from "./ProductEditorialIcon";

export type ProductPillarSection = NonNullable<EditorialContent["pillars"]>;

export interface ProductPillarsProps {
  section: ProductPillarSection;
  number?: string;
}

/**
 * Los pilares: por qué el producto hace lo que dice que hace.
 *
 * ── La cabecera va partida en dos ────────────────────────────────────
 * Título a la izquierda, entradilla a la derecha. Es la única cabecera de
 * la ficha que se abre así, y no es capricho: la entradilla de este
 * bloque es un párrafo largo —explica el marco entero— y debajo de un
 * título de 40 px se lee como una segunda introducción a la página. Al
 * lado, se lee como lo que es: la nota al pie del título.
 *
 * ── Cuatro columnas, y el disco de tinta ─────────────────────────────
 * Las tarjetas son claras sobre crema, y sin el disco oscuro del icono
 * la fila entera se desdibuja contra el fondo. El disco es el ancla.
 *
 * ── La inversión de pesos ────────────────────────────────────────────
 * El título de la sección va en 300 y el de cada tarjeta en 600. Al
 * revés de lo que uno escribiría: es lo que hace que el titular grande se
 * lea como voz editorial y las tarjetas como datos.
 *
 * ── El bloque llega o no llega, no llega vacío ───────────────────────
 * `section` es obligatorio: quien compone decide si existe. El esquema
 * garantiza al menos un elemento, así que aquí no hay ningún caso de
 * "título con nada debajo" que defender.
 */
export function ProductPillars({ section, number }: ProductPillarsProps) {
  const { t } = useTranslation();

  return (
    <section className="grid gap-10">
      <ProductBlockHeading
        eyebrow={t("Pilares")}
        number={number}
        title={section.title}
        intro={section.intro}
        layout="split"
      />

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {section.items.map((pillar) => (
          <li
            key={pillar.title}
            className="group rounded-[28px] border border-ds-border bg-white p-6 shadow-ds-xs transition hover:-translate-y-0.5 hover:shadow-ds-card-hv"
          >
            <ProductEditorialIcon name={pillar.icon} variant="pillar" />

            <h3 className="mt-4 text-[18px] font-semibold leading-[1.25] tracking-[-0.01em] text-ds-ink">
              {pillar.title}
            </h3>

            {pillar.description && (
              <p className="mt-1.5 text-[13px] font-light leading-[1.55] text-ds-ink-3">
                {pillar.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
